import { ChangeDetectionStrategy, Component, computed, inject } from '@angular/core';
import { DecimalPipe } from '@angular/common';
import { MatDialog } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { TranslateModule } from '@ngx-translate/core';
import { PlanPriceDialogComponent } from '../../Dialogs/plan-price-dialog/plan-price-dialog.component';
import { AvEmptyStateComponent } from '../../../../Shared/Components/av-empty-state/av-empty-state.component';
import { AvStatusChipComponent } from '../../../../Shared/Components/av-status-chip/av-status-chip.component';
import { PlanDetailFacade } from '../../../../../Facades/Plans/plan-detail.facade';
import { VALUE_UNRESOLVED } from '../../../../../Consts/backoffice.const';
import { OFFER_TYPE_LABEL } from '../../../../../Consts/plans.const';
import { PlanPrice } from '../../../../../Models/backoffice/plan-price.model';
import { PlanPriceDialogResult } from '../../../Interfaces/plan-dialog.interface';

@Component({
  selector: 'av-plan-prices',
  standalone: true,
  imports: [
    DecimalPipe,
    MatIconModule,
    MatTooltipModule,
    TranslateModule,
    AvEmptyStateComponent,
    AvStatusChipComponent,
  ],
  templateUrl: './plan-prices.component.html',
  styleUrl: './plan-prices.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: { class: 'd-block' },
})
export class PlanPricesComponent {
  private readonly _dialog = inject(MatDialog);

  readonly facade = inject(PlanDetailFacade);

  readonly unresolved = VALUE_UNRESOLVED;
  readonly offerLabelKeys = OFFER_TYPE_LABEL;

  readonly isEmpty = computed<boolean>(
    () => this.facade.pricesState() === 'ready' && this.facade.prices().length === 0,
  );

  readonly canAddPrice = computed<boolean>(
    () => this.facade.canWrite() && this.facade.components().some((component) => component.is_permitted),
  );

  offerLabelKey(offerType: string | null): string {
    return offerType ? (this.offerLabelKeys[offerType] ?? offerType) : this.unresolved;
  }

  openCreate(): void {
    const reference = this._dialog.open(PlanPriceDialogComponent, {
      data: {
        title: 'backoffice.plans.prices.dialog.create_title',
        components: this.facade.components(),
        defaultCurrency: this.facade.plan()?.currency ?? null,
        price: null,
      },
    });

    reference.afterClosed().subscribe((result?: PlanPriceDialogResult) => this._applyCreate(result));
  }

  openEdit(price: PlanPrice): void {
    const reference = this._dialog.open(PlanPriceDialogComponent, {
      data: {
        title: 'backoffice.plans.prices.dialog.edit_title',
        components: this.facade.components(),
        defaultCurrency: this.facade.plan()?.currency ?? null,
        price,
      },
    });

    reference
      .afterClosed()
      .subscribe((result?: PlanPriceDialogResult) => this._applyEdit(price.id, result));
  }

  deactivate(price: PlanPrice): void {
    this.facade.deactivatePrice(price.id);
  }

  private _applyCreate(result?: PlanPriceDialogResult): void {
    if (!result) {
      return;
    }

    this.facade.createPrice({
      component_type: result.componentType,
      offer_type: result.offerType,
      billing_interval: result.billingInterval,
      currency: result.currency,
      unit_amount: result.unitAmount,
      is_active: result.isActive,
    });
  }

  private _applyEdit(priceId: string, result?: PlanPriceDialogResult): void {
    if (!result) {
      return;
    }

    this.facade.updatePrice(priceId, {
      billing_interval: result.billingInterval,
      currency: result.currency,
      unit_amount: result.unitAmount,
      is_active: result.isActive,
    });
  }
}
